"use client";

import { useCallback, useSyncExternalStore } from "react";

// The server has no viewport, so `false` is what SSR renders. React reads this
// snapshot during hydration, which keeps the hydrated markup in agreement.
function getServerSnapshot() {
  return false;
}

/**
 * A media query is an external store, so it is read as one: the live value
 * comes straight from the store instead of being copied into state a render
 * later, and changing `query` re-reads it rather than keeping the previous
 * query's answer until the next `change` event. During hydration the value
 * above is used first, so the output matches the server render.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const media = globalThis.matchMedia(query);
      media.addEventListener("change", onStoreChange);

      return () => media.removeEventListener("change", onStoreChange);
    },
    [query],
  );

  // The snapshot is a boolean, so identity is automatic — handing back the
  // MediaQueryList would look like a new store value on every read.
  const getSnapshot = useCallback(
    () => globalThis.matchMedia(query).matches,
    [query],
  );

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
